import React from "react";

/**
 * AmbientBg — a full-viewport dreamy Afro Sci-Fi backdrop.
 * Static (no motion): deep obsidian base + gold sunrise bloom (bottom-left) +
 * cyan nebula bloom (top-right) + faint diagonal gradient sheen + noise grain.
 *
 * Mount ONCE near the top of a page's root layout. Uses `pointer-events-none`
 * and `fixed inset-0 -z-10` so it never blocks interactions.
 */
export function AmbientBg({ variant = "studio", className = "" }) {
  // Two flavors so pages don't all look identical:
  //   "studio"   → gold-heavy dawn (warm)
  //   "gallery"  → cyan-heavy nightscape (cool)
  //   "storyboard" → dual bloom (balanced)
  const palettes = {
    studio: {
      warm: "rgba(242, 201, 76, 0.16)",
      cool: "rgba(0, 229, 255, 0.10)",
      violet: "rgba(140, 80, 220, 0.09)",
    },
    gallery: {
      warm: "rgba(242, 201, 76, 0.08)",
      cool: "rgba(0, 229, 255, 0.18)",
      violet: "rgba(140, 80, 220, 0.12)",
    },
    storyboard: {
      warm: "rgba(255, 138, 76, 0.10)",
      cool: "rgba(0, 229, 255, 0.14)",
      violet: "rgba(140, 80, 220, 0.10)",
    },
  };
  const p = palettes[variant] || palettes.studio;

  return (
    <div
      aria-hidden
      className={`ambient-bg fixed inset-0 -z-10 pointer-events-none overflow-hidden ${className}`}
      data-testid={`ambient-bg-${variant}`}
    >
      {/* Base obsidian */}
      <div className="absolute inset-0" style={{ background: "#07070C" }} />

      {/* Bottom-left warm sunrise */}
      <div
        className="absolute rounded-full blur-3xl"
        style={{
          width: "70vw",
          height: "70vw",
          left: "-18vw",
          bottom: "-22vw",
          background: `radial-gradient(closest-side, ${p.warm}, transparent 70%)`,
        }}
      />

      {/* Top-right cyan nebula */}
      <div
        className="absolute rounded-full blur-3xl"
        style={{
          width: "60vw",
          height: "60vw",
          right: "-18vw",
          top: "-14vw",
          background: `radial-gradient(closest-side, ${p.cool}, transparent 70%)`,
        }}
      />

      {/* Mid violet nebula */}
      <div
        className="absolute rounded-full blur-3xl"
        style={{
          width: "50vw",
          height: "50vw",
          left: "40vw",
          top: "35vh",
          background: `radial-gradient(closest-side, ${p.violet}, transparent 70%)`,
        }}
      />

      {/* Diagonal sheen */}
      <div
        className="absolute inset-0 opacity-40"
        style={{
          background:
            "linear-gradient(135deg, transparent 0%, rgba(255,255,255,0.02) 40%, transparent 60%)",
        }}
      />

      {/* Vignette */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.55) 100%)",
        }}
      />

      {/* Fine noise grain (SVG data URI) */}
      <div
        className="absolute inset-0 opacity-[0.06] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160' viewBox='0 0 160 160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)' opacity='1'/></svg>\")",
          backgroundSize: "160px 160px",
        }}
      />

      {/* Faint hairline star dots (static SVG) — minimal, dream-like */}
      <svg
        className="absolute inset-0 w-full h-full opacity-30"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="none"
      >
        <defs>
          <radialGradient id="starGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="rgba(242,201,76,0.9)" />
            <stop offset="100%" stopColor="rgba(242,201,76,0)" />
          </radialGradient>
        </defs>
        {[
          [12, 18, 1], [23, 44, 0.8], [45, 12, 1], [67, 30, 0.7], [83, 22, 1.1],
          [8, 62, 0.7], [35, 74, 1], [58, 88, 0.9], [72, 70, 0.8], [91, 60, 1],
          [15, 90, 0.6], [26, 20, 0.7], [50, 55, 1.2], [77, 47, 0.7], [95, 85, 0.9],
          [40, 40, 0.7], [62, 60, 0.7], [88, 12, 0.7],
        ].map(([cx, cy, r], i) => (
          <circle
            key={i}
            cx={`${cx}%`}
            cy={`${cy}%`}
            r={r}
            fill="url(#starGlow)"
          />
        ))}
      </svg>
    </div>
  );
}

export default AmbientBg;
